import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { useDispatch } from "react-redux";
import { logout, follow } from "../../redux/userRedux";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import Navbar from "../../components/Navbar";
import UploadNote from "../../components/UploadNote";
import NoteCard from "../../components/NoteCard";
import Comment from "../../components/Comment";
import NoteCardSkeleton from "../../components/NoteCardSkeleton";
import { publicRequest } from "../../requestMethods";
import theme from "../../theme";
import { mobile } from "../../responsive";
import { IconPlus, IconDocument, IconFolder, IconLogOut, IconUsers } from "../../icons";
import communityImg from "../../assets/community.png";
import ltceLogo from "../../assets/ltce.png";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const Shell = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.bg};
`;


const Sidebar = styled.aside`
  width: ${theme.sidebarWidth};
  flex-shrink: 0;
  background: ${theme.sidebarBg};
  border-right: 1px solid ${theme.separator};
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 60;
  overflow-y: auto;
  ${mobile(`display: none;`)}
`;

const SidebarTop = styled.div`
  padding: 22px 18px 16px;
  border-bottom: 1px solid ${theme.separator};
`;

const LogoRow = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  margin-bottom: 2px;
`;

const LogoMark = styled.div`
  width: 36px;
  height: 36px;
  background: ${theme.primary};
  color: #fff;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  font-family: Georgia, serif;
  flex-shrink: 0;
`;

const LogoText = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${theme.textMain};
  line-height: 1.2;
`;


const Nav = styled.nav`
  padding: 14px 10px 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SidebarMiddle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  min-height: 0;
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? "700" : "500")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  text-decoration: none;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${theme.primaryLight};
    color: ${theme.primary};
  }
`;

const CommunityMainNavItem = styled(NavItem)`justify-content: flex-start;`;
const CommunityChevron = styled.span`
  width: 7px;
  height: 7px;
  display: inline-block;
  border-right: 1.8px solid currentColor;
  border-bottom: 1.8px solid currentColor;
  transform: ${(p) => (p.$open ? "rotate(-135deg)" : "rotate(45deg)")};
  margin-top: ${(p) => (p.$open ? "2px" : "-1px")};
  transition: transform 0.15s ease, margin-top 0.15s ease;
`;
const CommunityChevronHit = styled.span`
  margin-left: auto;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  &:hover { background: ${theme.primaryLight}; }
`;
const CommunitySubList = styled.div`
  margin: 4px 0 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const CommunitySubItem = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? theme.primaryLight : "transparent")};
  color: ${(p) => (p.$active ? theme.primary : theme.textMuted)};
  padding: 7px 10px;
  font-size: 12px;
  font-weight: ${(p) => (p.$active ? "700" : "600")};
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  &:hover { background: ${theme.primaryLight}; color: ${theme.primary}; }
`;
const CommunitySubAvatar = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  object-fit: cover;
  flex-shrink: 0;
`;

const NewArtifactBtn = styled.button`
  margin: 0 10px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${theme.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 11px 0;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  &:hover { background: ${theme.primaryDark}; transform: translateY(-1px); }
`;

const SidebarUser = styled.div`
  padding: 14px 16px;
  border-top: 1px solid ${theme.separator};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: ${theme.primary};
  cursor: pointer;
`;

const UserAvatarImg = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: ${theme.textMain};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const LogoutBtn = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${theme.danger};
  display: flex;
  align-items: center;
  border-radius: 6px;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.15s, background 0.15s;
  &:hover { opacity: 1; background: ${theme.dangerLight}; }
`;


const StatsWidget = styled.div`
  margin: 0 10px;
  padding: 0 0 10px;
`;

const StatsWidgetLabel = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.textMuted};
  padding: 0 4px;
  margin-bottom: 10px;
`;

const ChartWrap = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 10px;
  padding: 28px 10px 4px;
  margin-bottom: 10px;
  position: relative;
`;

const ChartBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: ${theme.bg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 10px;
  font-weight: 700;
  color: ${theme.textMuted};
  z-index: 1;
`;

const QuickStatsBox = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.cardBorder};
  border-radius: 10px;
  padding: 12px 12px 6px;
`;

const QuickStatsTitle = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.textMain};
  margin-bottom: 10px;
`;

const StatRow = styled.div`
  margin-bottom: 10px;
`;

const StatRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const StatRowLabel = styled.span`
  font-size: 11.5px;
  color: ${theme.textSub};
  font-weight: 500;
`;

const StatRowValue = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${theme.textMain};
`;

const StatBar = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${theme.bgAlt};
  overflow: hidden;
`;

const StatBarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${theme.primary};
  width: ${(p) => p.$pct}%;
  transition: width 0.6s ease;
`;

const InlineStatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  border-top: 1px solid ${theme.separator};
`;

const InlineStatLabel = styled.span`
  font-size: 11.5px;
  color: ${theme.textSub};
  font-weight: 500;
`;

const InlineStatValue = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${theme.primary};
`;


const Main = styled.div`
  margin-left: ${theme.sidebarWidth};
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${mobile(`margin-left: 0;`)}
`;


const FeedArea = styled.div`
  padding: 32px 32px 80px;
  ${mobile(`padding: 20px 14px 80px;`)}
`;

const FeedMeta = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.primary};
  margin-bottom: 6px;
`;

const FeedHeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const FeedHeading = styled.h1`
  font-size: 26px;
  font-weight: 800;
  color: ${theme.textMain};
  margin: 0;
  letter-spacing: -0.5px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 6px;
`;

const FilterBtn = styled.button`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1.5px solid ${(p) => (p.$active ? theme.primary : theme.cardBorder)};
  background: ${(p) => (p.$active ? theme.primary : "transparent")};
  color: ${(p) => (p.$active ? "#fff" : theme.textMuted)};
  transition: all 0.15s;
  &:hover { border-color: ${theme.primary}; color: ${(p) => (p.$active ? "#fff" : theme.primary)}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  ${mobile(`grid-template-columns: 1fr;`)}

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatusText = styled.p`
  font-size: 14px;
  color: ${(p) => (p.$error ? theme.danger : theme.textMuted)};
  text-align: center;
  padding: 40px 0;
`;


const CommentSection = styled.div`
  background: ${theme.bgAlt};
  border-radius: 0 0 12px 12px;
  border: 1.5px solid ${theme.cardBorder};
  border-top: none;
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: -6px;
`;

const CommentInputRow = styled.form`
  display: flex;
  gap: 10px;
`;

const CommentInput = styled.input`
  flex: 1;
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 7px;
  padding: 8px 14px;
  font-size: 13px;
  font-family: inherit;
  color: ${theme.textMain};
  outline: none;
  &::placeholder { color: ${theme.textMuted}; }
  &:focus { border-color: ${theme.primary}; box-shadow: 0 0 0 3px ${theme.primaryGlow}; }
`;

const SendButton = styled.button`
  background: ${theme.primary};
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${theme.primaryDark}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CommentNote = styled.p`
  font-size: 13px;
  color: ${theme.textMuted};
`;

const NoteCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.3s ease both;
`;


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 18, 8, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  overflow-y: auto;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(26, 18, 8, 0.22);
  & > div { margin-bottom: 0 !important; border-radius: 14px; box-shadow: none; border: none; }
`;


const SuggestionsWrap = styled.div`
  max-width: 560px;
  margin: 0 auto;
`;

const SuggestHeading = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: ${theme.textMain};
  margin: 0 0 4px;
  letter-spacing: -0.3px;
`;

const SuggestSub = styled.p`
  font-size: 13px;
  color: ${theme.textMuted};
  margin: 0 0 24px;
`;

const SuggestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 28px;
  ${mobile(`grid-template-columns: 1fr;`)}
`;

const SuggestCard = styled.div`
  background: ${theme.cardBg};
  border: 1.5px solid ${theme.cardBorder};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 4px 16px rgba(26,18,8,0.08); }
`;

const SuggestAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.bgAlt};
  border: 1.5px solid ${theme.cardBorder};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
  color: ${theme.primary};
  flex-shrink: 0;
  overflow: hidden;
`;

const SuggestAvatarImg = styled.img`width: 100%; height: 100%; object-fit: cover;`;

const SuggestInfo = styled.div`flex: 1; min-width: 0;`;

const SuggestName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${theme.textMain};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SuggestMeta = styled.div`
  font-size: 11px;
  color: ${theme.textMuted};
  margin-top: 1px;
`;

const SuggestFollowBtn = styled.button`
  flex-shrink: 0;
  background: ${(p) => (p.$following ? theme.bgAlt : theme.primary)};
  color: ${(p) => (p.$following ? theme.textMuted : "#fff")};
  border: 1.5px solid ${(p) => (p.$following ? theme.cardBorder : theme.primary)};
  border-radius: 7px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  cursor: ${(p) => (p.$following ? "default" : "pointer")};
  transition: background 0.15s;
  &:hover:not(:disabled) { background: ${(p) => (p.$following ? theme.bgAlt : theme.primaryDark)}; }
`;

const UploadPrompt = styled.div`
  background: ${theme.cardBg};
  border: 1.5px dashed ${theme.cardBorder};
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  margin-bottom: 28px;
`;

const UploadPromptText = styled.p`
  font-size: 13.5px;
  color: ${theme.textMuted};
  margin: 0 0 14px;
`;

const UploadPromptBtn = styled.button`
  background: ${theme.primary};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  &:hover { background: ${theme.primaryDark}; }
`;


const FAB = styled.button`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${theme.primary};
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(160, 98, 10, 0.35);
  transition: background 0.15s, transform 0.1s;
  z-index: 100;
  &:hover { background: ${theme.primaryDark}; transform: scale(1.06); }
`;

function InlineComments({ noteId, onCommentAdded }) {
  const currentUser = useSelector((s) => s.user.currentUser);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicRequest.get(`/comments/${noteId}`)
      .then((res) => { if (!cancelled) setComments(res.data); })
      .catch(() => { if (!cancelled) setError("Failed to load comments."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [noteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const res = await publicRequest.post("/comments", { userId: currentUser._id, noteId, text: text.trim() });
      setComments((prev) => [{ ...res.data, userId: { _id: currentUser._id, username: currentUser.username } }, ...prev]);
      if (onCommentAdded) onCommentAdded();
      setText("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to post comment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CommentSection>
      <CommentInputRow onSubmit={handleSubmit}>
        <CommentInput type="text" placeholder="Write a comment…" value={text} onChange={(e) => setText(e.target.value)} />
        <SendButton type="submit" disabled={submitting || !text.trim()}>{submitting ? "…" : "Post"}</SendButton>
      </CommentInputRow>
      {error && <CommentNote style={{ color: theme.danger }}>{error}</CommentNote>}
      <CommentList>
        {loading ? <CommentNote>Loading…</CommentNote>
          : comments.length === 0 ? <CommentNote>No comments yet.</CommentNote>
          : comments.map((c) => <Comment key={c._id} comment={c} />)}
      </CommentList>
    </CommentSection>
  );
}

export default function Home() {
  const currentUser = useSelector((s) => s.user.currentUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openCommentNoteId, setOpenCommentNoteId] = useState(null);
  const [commentCounts, setCommentCounts] = useState({});
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState("popular");
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [myNotes, setMyNotes] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [myCommunity, setMyCommunity] = useState(null);
  const [communityMenuOpen, setCommunityMenuOpen] = useState(false);
  const followingSet = new Set(currentUser?.followings || []);


  const totalLikes = myNotes.reduce((s, n) => s + (n.likes?.length || 0), 0);
  const ratedNotes = myNotes.filter((n) => n.ratings?.length > 0);
  const avgRating = ratedNotes.length
    ? (ratedNotes.reduce((s, n) => s + n.ratings.reduce((a, r) => a + r.value, 0) / n.ratings.length, 0) / ratedNotes.length).toFixed(1)
    : null;


  const sparkData = (() => {
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return { day: d.toLocaleDateString("en", { weekday: "short" }), count: 0 };
    });
    myNotes.forEach((n) => {
      const created = new Date(n.createdAt);
      const daysAgo = Math.floor((Date.now() - created) / 86400000);
      if (daysAgo < 7) days[6 - daysAgo].count += 1;
    });
    return days;
  })();

  const STATIC_URL = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/api").replace("/api", "");
  const avatarSrc = currentUser?.profilePicture ? `${STATIC_URL}/images/${currentUser.profilePicture}` : null;

  const fetchTimeline = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      const res = await publicRequest.get(`/notes/timeline/${currentUser._id}`);
      setNotes(res.data);
      const counts = {};
      await Promise.all(res.data.map(async (note) => {
        try { const cr = await publicRequest.get(`/comments/${note._id}`); counts[note._id] = cr.data.length; }
        catch { counts[note._id] = 0; }
      }));
      setCommentCounts(counts);
    } catch { setError("Failed to load timeline."); }
    finally { setLoading(false); }
  }, [currentUser]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await publicRequest.get("/community");
        setCommunities(r.data || []);
      } catch {
        setCommunities([]);
      }
    };
    run();
  }, []);

  useEffect(() => {
    if (!currentUser?.institution) return;
    publicRequest.get(`/community/institution/${encodeURIComponent(currentUser.institution)}`)
      .then((r) => {
        setMyCommunity(r.data);
        setCommunities((prev) => prev.find((c) => c._id === r.data._id) ? prev : [r.data, ...prev]);
      })
      .catch(() => {});
  }, [currentUser]);


  useEffect(() => {
    if (!currentUser) return;
    publicRequest.get(`/notes/profile/${currentUser._id}`)
      .then((res) => setMyNotes(res.data))
      .catch(() => {});
  }, [currentUser]);


  useEffect(() => {
    if (!currentUser || loading || notes.length > 0) return;
    const run = async () => {
      try {

        const meRes = await publicRequest.get(`/users/${currentUser._id}`);
        const freshFollowings = meRes.data.followings || [];
        const res = await publicRequest.get(`/users/findusers/a`);
        const others = res.data
          .filter((u) => u._id !== currentUser._id && !freshFollowings.includes(u._id))
          .slice(0, 6);
        setSuggestedUsers(others);
      } catch {  }
    };
    run();
  }, [currentUser, loading, notes.length]);

  const handleFollowSuggested = async (userId) => {
    try {
      await publicRequest.put(`/users/${userId}/follow`, { userId: currentUser._id });
      dispatch(follow(userId));
    } catch {  }
  };

  const isMember = (community) => community?.members?.some((m) => m === currentUser?._id || m?._id === currentUser?._id || m?.toString() === currentUser?._id);
  const isLTCECommunity = (community) => {
    const target = `${community?.name || ""} ${community?.institution || ""}`.toUpperCase();
    return target.includes("LTCE");
  };
  const getCommunityLogo = (community) => isLTCECommunity(community) ? ltceLogo : communityImg;
  const communityNavList = (() => {
    const items = [];
    if (myCommunity) items.push(myCommunity);
    communities.forEach((c) => {
      if (isMember(c) && !items.find((x) => x._id === c._id)) items.push(c);
    });
    return items;
  })();

  const handleNoteCreated = (n) => {
    const noteWithAuthor = {
      ...n,
      author: { _id: currentUser._id, username: currentUser.username, profilePicture: currentUser.profilePicture || null },
    };
    setNotes((p) => [noteWithAuthor, ...p]);
    setMyNotes((p) => [n, ...p]);
    setCommentCounts((p) => ({ ...p, [n._id]: 0 }));
    setShowUpload(false);
  };
  const handleNoteDeleted = (id) => {
    setNotes((p) => p.filter((n) => n._id !== id));
    setMyNotes((p) => p.filter((n) => n._id !== id));
  };
  const handleNoteEdited = (u) => setNotes((p) => p.map((n) => (n._id === u._id ? { ...n, ...u } : n)));
  const handleCommentClick = (id) => setOpenCommentNoteId((p) => (p === id ? null : id));
  const handleCommentAdded = (id) => setCommentCounts((p) => ({ ...p, [id]: (p[id] || 0) + 1 }));

  const sortedNotes = [...notes].sort((a, b) => {
    if (filter === "recent") {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }

    const scoreA = (a.views?.length || 0) + (a.likes?.length || 0) * 2;
    const scoreB = (b.views?.length || 0) + (b.likes?.length || 0) * 2;
    return scoreB - scoreA;
  });

  return (
    <Shell>
      {}
      <Sidebar>
        <SidebarTop>
          <LogoRow to="/">
            <LogoMark>N</LogoMark>
            <div>
              <LogoText>NotesHub</LogoText>
            </div>
          </LogoRow>
        </SidebarTop>

        <Nav>
          <NavItem to="/" $active={location.pathname === "/"}>
            <IconDocument size="15px" /> Feed
          </NavItem>
          {currentUser && (
            <NavItem to="/folders" $active={location.pathname === "/folders"}>
              <IconFolder size="15px" /> Folders
            </NavItem>
          )}
          <CommunityMainNavItem to="/community" $active={location.pathname === "/community"}>
            <IconUsers size="15px" /> Community
            <CommunityChevronHit
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCommunityMenuOpen((v) => !v);
              }}
              aria-label="Toggle my communities"
              title="Toggle my communities"
            >
              <CommunityChevron $open={communityMenuOpen} />
            </CommunityChevronHit>
          </CommunityMainNavItem>
          {communityMenuOpen && (
            <CommunitySubList>
              {communityNavList.length === 0 ? (
                <CommunitySubItem type="button" disabled style={{ opacity: 0.6, cursor: "default" }}>
                  No communities yet
                </CommunitySubItem>
              ) : communityNavList.map((c) => (
                <CommunitySubItem key={c._id} type="button" onClick={() => navigate("/community") }>
                  <CommunitySubAvatar src={getCommunityLogo(c)} alt={`Logo - ${c.institution || c.name}`} />
                  {c.name}
                </CommunitySubItem>
              ))}
            </CommunitySubList>
          )}
        </Nav>

        {}
        <SidebarMiddle>
          {currentUser && !loading && (
            <StatsWidget>
              <StatsWidgetLabel>Activity</StatsWidgetLabel>
              <ChartWrap>
                <ChartBadge>{myNotes.length} notes</ChartBadge>
                <ResponsiveContainer width="100%" height={60}>
                  <AreaChart data={sparkData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#5aa7d4" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#5aa7d4" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="count" stroke="#5aa7d4" strokeWidth={2}
                      fill="url(#sparkGrad)" dot={false} isAnimationActive={true} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartWrap>
              <QuickStatsBox>
                <QuickStatsTitle>Quick Stats</QuickStatsTitle>
                <StatRow>
                  <StatRowTop>
                    <StatRowLabel>Notes Shared</StatRowLabel>
                    <StatRowValue>{myNotes.length}</StatRowValue>
                  </StatRowTop>
                  <StatBar><StatBarFill $pct={Math.min(myNotes.length * 10, 100)} /></StatBar>
                </StatRow>
                <InlineStatRow>
                  <InlineStatLabel>Followers</InlineStatLabel>
                  <InlineStatValue>{currentUser.followers?.length ?? 0}</InlineStatValue>
                </InlineStatRow>
                <InlineStatRow>
                  <InlineStatLabel>Total Likes</InlineStatLabel>
                  <InlineStatValue>{totalLikes}</InlineStatValue>
                </InlineStatRow>
                {avgRating && (
                  <InlineStatRow>
                    <InlineStatLabel>Avg Rating</InlineStatLabel>
                    <InlineStatValue>★ {avgRating}</InlineStatValue>
                  </InlineStatRow>
                )}
              </QuickStatsBox>
            </StatsWidget>
          )}
        </SidebarMiddle>

        {}
        <NewArtifactBtn onClick={() => setShowUpload(true)}>
          <IconPlus size="14px" /> New Note
        </NewArtifactBtn>

        {currentUser && (
          <SidebarUser>
            <UserAvatar onClick={() => navigate(`/profile/${currentUser._id}`)}>
              {avatarSrc ? <UserAvatarImg src={avatarSrc} alt={currentUser.username} /> : currentUser.username?.[0]?.toUpperCase()}
            </UserAvatar>
            <UserInfo>
              <UserName>{currentUser.username}</UserName>
              <UserRole>Student</UserRole>
            </UserInfo>
            <LogoutBtn onClick={() => dispatch(logout())} title="Sign out">
              <IconLogOut size="15px" />
            </LogoutBtn>
          </SidebarUser>
        )}
      </Sidebar>

      {}
      <Main>
        <Navbar />
        <FeedArea>
          <FeedMeta>Student Stream</FeedMeta>
          <FeedHeadRow>
            <FeedHeading>Timeline Feed</FeedHeading>
            <FilterRow>
              <FilterBtn $active={filter === "recent"} onClick={() => setFilter("recent")}>Recent</FilterBtn>
              <FilterBtn $active={filter === "popular"} onClick={() => setFilter("popular")}>Popular</FilterBtn>
            </FilterRow>
          </FeedHeadRow>

          {loading ? (
            <Grid>
              {Array.from({ length: 6 }).map((_, i) => (
                <NoteCardSkeleton key={i} />
              ))}
            </Grid>
          ) : error ? (
            <StatusText $error>{error}</StatusText>
          ) : notes.length === 0 ? (
            <>
              <SuggestionsWrap>
                <UploadPrompt>
                  <UploadPromptText>You haven't shared any notes yet. Upload your first one!</UploadPromptText>
                  <UploadPromptBtn onClick={() => setShowUpload(true)}>
                    <IconPlus size="13px" /> Share a Note
                  </UploadPromptBtn>
                </UploadPrompt>

                {suggestedUsers.filter((u) => !followingSet.has(u._id)).length > 0 && (
                  <>
                    <SuggestHeading>People to follow</SuggestHeading>
                    <SuggestSub>Follow other students to see their notes in your feed.</SuggestSub>
                    <SuggestGrid>
                      {suggestedUsers.filter((u) => !followingSet.has(u._id)).map((u) => {
                        const uAvatarSrc = u.profilePicture
                          ? `${STATIC_URL}/images/${u.profilePicture}` : null;
                        const isFollowed = followingSet.has(u._id);
                        return (
                          <SuggestCard key={u._id}>
                            <SuggestAvatar onClick={() => navigate(`/profile/${u._id}`)}>
                              {uAvatarSrc
                                ? <SuggestAvatarImg src={uAvatarSrc} alt={u.username} />
                                : u.username?.[0]?.toUpperCase()}
                            </SuggestAvatar>
                            <SuggestInfo>
                              <SuggestName
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/profile/${u._id}`)}
                              >
                                {u.username}
                              </SuggestName>
                              <SuggestMeta>
                                {u.institution || "Student"} · {u.followers?.length ?? 0} followers
                              </SuggestMeta>
                            </SuggestInfo>
                            <SuggestFollowBtn
                              $following={isFollowed}
                              onClick={() => !isFollowed && handleFollowSuggested(u._id)}
                            >
                              {isFollowed ? "Following" : "Follow"}
                            </SuggestFollowBtn>
                          </SuggestCard>
                        );
                      })}
                    </SuggestGrid>
                  </>
                )}
              </SuggestionsWrap>
            </>
          ) : (
            <Grid>
              {sortedNotes.map((note) => (
                <NoteCardWrapper key={note._id}>
                  <NoteCard
                    note={note}
                    onCommentClick={handleCommentClick}
                    commentCount={commentCounts[note._id] || 0}
                    onDeleted={handleNoteDeleted}
                    onEdited={handleNoteEdited}
                  />
                  {openCommentNoteId === note._id && (
                    <InlineComments noteId={note._id} onCommentAdded={() => handleCommentAdded(note._id)} />
                  )}
                </NoteCardWrapper>
              ))}
            </Grid>
          )}
        </FeedArea>
      </Main>

      {}
      {showUpload && (
        <Overlay onClick={() => setShowUpload(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <UploadNote onNoteCreated={handleNoteCreated} onCancel={() => setShowUpload(false)} />
          </ModalBox>
        </Overlay>
      )}

      {}
      <FAB onClick={() => setShowUpload(true)} aria-label="New note" style={{ display: "none" }}>
        <IconPlus size="20px" />
      </FAB>
    </Shell>
  );
}
